/**
 * Rastreio de Links de Campanhas.
 *
 * - Atribuição: a tag de `?origem=` (ou `utm_source`, `fbclid`, `gclid`) é
 *   guardada por 30 dias no navegador. Assim, quem entra pelo link na home e
 *   só se cadastra depois (ou em outra visita) continua atribuído à campanha.
 * - Sessão: uma por aba, renovada após 30 min sem atividade.
 * - Eventos vão em lote para o monolito (html/api/campanhas/eventos.php).
 *
 * Nenhum valor digitado é enviado: só nome do campo, tempo e se foi preenchido.
 */

const ENDPOINT = 'https://moovery.app/api/campanhas/eventos.php'

const CHAVE_ATRIBUICAO = 'moovery_campanha'
const CHAVE_VISITANTE = 'moovery_visitante'
const CHAVE_SESSAO = 'moovery_sessao'

const VALIDADE_ATRIBUICAO_MS = 30 * 24 * 60 * 60 * 1000
const EXPIRACAO_SESSAO_MS = 30 * 60 * 1000
const INTERVALO_ENVIO_MS = 4000
const MAX_FILA = 20

let fila = []
let timerEnvio = null
let listenersRegistrados = false

function lerStorage(storage, chave) {
  try {
    const bruto = storage.getItem(chave)
    return bruto ? JSON.parse(bruto) : null
  } catch (e) {
    return null
  }
}

function gravarStorage(storage, chave, valor) {
  try {
    storage.setItem(chave, JSON.stringify(valor))
  } catch (e) {
    // modo privado / storage cheio: segue sem persistir
  }
}

function gerarUuid() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Mesma regra do backend: minúsculas, [a-z0-9_-], até 60 caracteres. */
export function normalizarOrigem(valor) {
  if (!valor) return null
  const limpo = String(valor)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, '')
    .slice(0, 60)
  return limpo || null
}

function visitanteId() {
  let id = lerStorage(window.localStorage, CHAVE_VISITANTE)
  if (!id) {
    id = gerarUuid()
    gravarStorage(window.localStorage, CHAVE_VISITANTE, id)
  }
  return id
}

function sessao() {
  const agora = Date.now()
  let atual = lerStorage(window.sessionStorage, CHAVE_SESSAO)
  if (!atual || agora - atual.ultimaAtividade > EXPIRACAO_SESSAO_MS) {
    atual = {
      id: gerarUuid(),
      inicio: agora,
      referrer: document.referrer || null,
      paginaEntrada: window.location.pathname + window.location.search
    }
  }
  atual.ultimaAtividade = agora
  gravarStorage(window.sessionStorage, CHAVE_SESSAO, atual)
  return atual
}

/**
 * Lê a origem da URL atual e atualiza a atribuição (último clique vence).
 * Retorna a atribuição vigente ou null.
 */
export function capturarOrigem(search = window.location.search) {
  const params = new URLSearchParams(search)
  let origem = normalizarOrigem(params.get('origem') || params.get('utm_source'))
  if (!origem && params.get('fbclid')) origem = 'facebook'
  if (!origem && params.get('gclid')) origem = 'google'

  if (origem) {
    const atribuicao = {
      origem,
      utm: {
        source: params.get('utm_source'),
        medium: params.get('utm_medium'),
        campaign: params.get('utm_campaign'),
        content: params.get('utm_content'),
        term: params.get('utm_term')
      },
      em: Date.now()
    }
    gravarStorage(window.localStorage, CHAVE_ATRIBUICAO, atribuicao)
    return atribuicao
  }

  const salva = lerStorage(window.localStorage, CHAVE_ATRIBUICAO)
  if (salva && Date.now() - salva.em <= VALIDADE_ATRIBUICAO_MS) return salva
  return null
}

/** Tag da campanha vigente (para enviar junto com o cadastro). */
export function origemAtual() {
  const atribuicao = capturarOrigem()
  return atribuicao ? atribuicao.origem : null
}

export function sessaoAtualId() {
  return sessao().id
}

function dispositivo() {
  const largura = window.innerWidth || 1024
  if (largura < 768) return 'mobile'
  if (largura < 1024) return 'tablet'
  return 'desktop'
}

function navegador() {
  const ua = navigator.userAgent || ''
  if (/edg\//i.test(ua)) return 'edge'
  if (/opr\//i.test(ua)) return 'opera'
  if (/samsungbrowser/i.test(ua)) return 'samsung'
  if (/chrome|crios/i.test(ua)) return 'chrome'
  if (/firefox|fxios/i.test(ua)) return 'firefox'
  if (/safari/i.test(ua)) return 'safari'
  return 'outro'
}

function montarLote(eventos) {
  const s = sessao()
  const atribuicao = capturarOrigem()
  return {
    sessao_id: s.id,
    visitante_id: visitanteId(),
    origem: atribuicao ? atribuicao.origem : null,
    utm: atribuicao ? atribuicao.utm : null,
    referrer: s.referrer,
    pagina_entrada: s.paginaEntrada,
    dispositivo: dispositivo(),
    navegador: navegador(),
    eventos
  }
}

/** Envia a fila. `saindo` usa sendBeacon, que sobrevive ao fechamento da aba. */
export function enviarFila(saindo = false) {
  if (timerEnvio) {
    clearTimeout(timerEnvio)
    timerEnvio = null
  }
  if (fila.length === 0) return

  const eventos = fila
  fila = []
  // text/plain evita o preflight de CORS.
  const corpo = JSON.stringify(montarLote(eventos))

  try {
    if (saindo && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([corpo], { type: 'text/plain' }))
      return
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: corpo,
      keepalive: true
    }).catch(() => {})
  } catch (e) {
    // rastreio nunca pode quebrar o site
  }
}

function registrarListeners() {
  if (listenersRegistrados) return
  listenersRegistrados = true
  const aoSair = () => enviarFila(true)
  window.addEventListener('pagehide', aoSair)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') aoSair()
  })
}

/**
 * Registra um evento. Tipos aceitos pelo backend: page_view, scroll, cta_click,
 * form_view, field_focus, field_fill, field_error, step1_submit, step1_success,
 * step1_error, step2_submit, step2_success, step2_error, form_abandon.
 */
export function rastrear(tipo, { pagina, campo, dados } = {}) {
  try {
    registrarListeners()
    fila.push({
      tipo,
      pagina: pagina || window.location.pathname,
      campo: campo || null,
      dados: dados || null,
      ts: Date.now()
    })
    if (fila.length >= MAX_FILA) {
      enviarFila()
    } else if (!timerEnvio) {
      timerEnvio = setTimeout(() => enviarFila(), INTERVALO_ENVIO_MS)
    }
  } catch (e) {
    // rastreio nunca pode quebrar o site
  }
}
